import { deepmerge } from "@fastify/deepmerge";
import { Task } from "@lit/task";
import {
    DASHBOARD_CONFIG,
    dateAdd,
    dateSub,
    defaultDashboardConfig,
    formatDate,
    getContractDeadlineEvents,
    getNextDay,
    getPrevDay,
    getToday,
} from "@pentacode/core";
import "@pentacode/ui/components";
import { css, html, LitElement } from "lit";
import { customElement, property } from "lit/decorators.js";
import { createRef, type Ref, ref } from "lit/directives/ref.js";
import "../../../../components/avatar/avatar";
import "../../../../elements/absence-dialog";
import { app, router } from "../../../../init";
import { shared } from "../../../../styles";
import { RelativeDayFormatter } from "../../../../utils/temporal.util";
import "./base-tile";
import type { DashboardTile } from "./base-tile";

export type ContractDeadlinesTileSettings = {
    lookaheadDays: number;
};

const mergeDashboardSettings = deepmerge();

@customElement("ptc-dashboard-tile-contract-deadlines")
export class DashboardTileContractDeadlines extends LitElement {
    @property({ attribute: false })
    size: { inline: number; block: number } = app.settings.dashboard.tiles.contractDeadlines.size;

    @property({ type: Object })
    settings: ContractDeadlinesTileSettings = app.settings.dashboard.tiles.contractDeadlines.settings!;

    static styles = [
        shared,
        css`
            :host {
                display: contents;
            }

            [part="employee-list"] {
                display: flex;
                flex-direction: column;
                gap: 1rem;
                padding: 1rem;
                margin: 0;
            }

            [part="employee"] {
                display: grid;
                gap: 0.25rem 0.5rem;
                grid-template-columns: min-content 1fr;
                grid-template-rows: auto auto auto;
                place-items: center start;
            }

            [part="employee-avatar"] {
                grid-row: 1 / span 3;
            }

            [part="employee-name"] {
                font-size: 1.15em;
                font-weight: 600;
                transition: color 0.2s ease-in-out;
            }

            [part="employee"]:hover {
                cursor: pointer;
            }
            [part="employee"]:hover [part="employee-name"] {
                color: var(--blue);
            }

            [part="details"] {
                display: flex;
                flex-direction: column;
                gap: 0.5rem;
                justify-content: center;
                min-height: 1.5rem;
            }

            [part="time-left"] {
                width: fit-content;
                grid-column: 2;
            }

            [part="event-type-badge"] {
                display: inline-flex;
                gap: 0.125em;
                align-items: center;
            }
        `,
    ];

    private _expiringContractsTask = new Task(this, {
        task: async ([lookaheadDays]) => {
            // compensate fort inclusive date
            const cutOffStartDate = getNextDay(getToday());
            // +1 because the date in the database is inclusive and consider the current day
            const cutoffEndDate = dateAdd(cutOffStartDate, { days: lookaheadDays + 1 });
            const dateRange = { from: cutOffStartDate, to: cutoffEndDate };

            return getContractDeadlineEvents(app.employees, dateRange);
        },

        args: () => [this.settings.lookaheadDays] as const,
    });

    private _formRef: Ref<HTMLFormElement> = createRef();
    private _tileRef: Ref<DashboardTile> = createRef();

    private _saveSettings(ev: SubmitEvent) {
        ev.preventDefault();

        const data = new FormData(this._formRef.value);
        const lookaheadDays = Number(data.get("lookaheadDays"));
        this.settings.lookaheadDays = lookaheadDays;

        app.updateSettings({
            dashboard: mergeDashboardSettings(app.settings.dashboard, {
                tiles: {
                    contractDeadlines: { settings: { lookaheadDays } },
                },
            }),
        })
            .then(() => {
                this.requestUpdate();

                if (this._tileRef.value) {
                    this._tileRef.value.settingsOpen = false;
                }
            })
            .catch((e) => {
                console.error(e);
            });
    }

    private _renderSettings() {
        return html`<form
            slot="settings"
            ${ref(this._formRef)}
            @submit=${this._saveSettings}
            id="tile.contractDeadlines"
        >
            <div class="field">
                <label for="lookaheadDays">Anzahl Tage</label>
                <input
                    id="tile.contractDeadlines.lookaheadDays"
                    name="lookaheadDays"
                    type="number"
                    min="0"
                    max="365"
                    placeholder=${defaultDashboardConfig.tiles.contractDeadlines.settings!.lookaheadDays.toString()}
                    .value=${app.settings.dashboard.tiles.contractDeadlines.settings!.lookaheadDays.toString()}
                    .defaultValue=${defaultDashboardConfig.tiles.contractDeadlines.settings!.lookaheadDays.toString()}
                />
            </div>

            <!-- workaround for some browsers needing a explicit submit element -->
            <input type="submit" style="display: none;" />
        </form>`;
    }

    render() {
        return html`
            <ptc-dashboard-tile
                ${ref(this._tileRef)}
                label=${DASHBOARD_CONFIG.tiles.contractDeadlines.label}
                .subLabel=${`in den nächsten ${this.settings.lookaheadDays} Tagen`}
                .size=${this.size}
                .settingsForm=${this._formRef.value}
                icon="icon.object.contract"
                tileType="contractDeadlines"
                @tile-size-change=${(e: CustomEvent) => {
                    this.size = e.detail.size;
                }}
            >
                ${this._expiringContractsTask.render({
                    initial: () => html``,
                    pending: () => html`
                        <ptc-ui-spinner
                            class="double-padded place-self-center"
                            active
                            style="font-size: 1rem"
                        ></ptc-ui-spinner>
                    `,
                    error: () =>
                        html`<div class="double-padded text-centering">Verträge konnten nicht geladen werden!</div>`,
                    complete: (contractDeadlineEvents) => {
                        if (!contractDeadlineEvents.length) {
                            return html`<div class="double-padded text-centering subtle place-self-center">
                                <span class="emoji huger">👏</span>
                                <p>Alle Verträge im Griff</p>
                            </div>`;
                        }

                        return html` <ul part="employee-list">
                            ${contractDeadlineEvents.map((entry) => {
                                // calculate the date one day before the contract expiration date (exclusive date end)
                                const date = entry.date ? getPrevDay(entry.date) : null;
                                const daysLeft = date ? dateSub(getToday(), date) : 0;

                                return html`
                                    <li
                                        part="employee"
                                        @click=${() => {
                                            router.go(
                                                `employees/${entry.employee.id}/contracts/view/${entry.contract?.id}`
                                            );
                                        }}
                                    >
                                        <ptc-avatar .employee=${entry.employee} part="employee-avatar"></ptc-avatar>
                                        <span class="semibold" part="employee-name">${entry.employee.name}</span>
                                        <div
                                            part="event-type-badge"
                                            class="tiny pill ${entry.type === "probationEnd" ? "" : "orange"}"
                                        >
                                            ${entry.type === "probationEnd"
                                                ? html`<ptc-ui-icon name="icon.object.target-hours"></ptc-ui-icon> Ende
                                                      der Probezeit`
                                                : html`<ptc-ui-icon name="icon.object.contract"></ptc-ui-icon>
                                                      Vertragsende`}
                                        </div>

                                        <span part="time-left">
                                            <span
                                                part="time-left-label"
                                                id="timeLeft-${entry.type}-${entry.employee.id}"
                                            >
                                                ${RelativeDayFormatter.format(daysLeft, "days")}
                                            </span>

                                            <ptc-ui-tooltip
                                                for="timeLeft-${entry.type}-${entry.employee.id}"
                                                placement="right"
                                            >
                                                ${date ? formatDate(new Date(date)) : ""}
                                            </ptc-ui-tooltip>
                                        </span>
                                    </li>
                                `;
                            })}
                        </ul>`;
                    },
                })}
                ${this._renderSettings()}
            </ptc-dashboard-tile>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ptc-dashboard-tile-contract-deadlines": DashboardTileContractDeadlines;
    }
}
